import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import {
  bankReconApi,
  type AdjustmentType,
  type BankAccount,
  type BankStatementEntry,
  type ReconciliationWorkspace,
  type ReconciliationHistoryItem,
  type StatementImportRow,
} from '@/api/bankReconApi';
import { financeApi, type CoaAccount } from '@/api/financeApi';
import { getAllParties, type PartyResponse } from '@/api/party.api';
import { formatINR } from '@/utils/format';

type TabKey = 'reconciliation' | 'cheques' | 'history';
type StatementFilter = 'ALL' | 'OPEN' | 'SUGGESTED' | 'MATCHED' | 'EXCLUDED';
type BookFilter = 'ALL' | 'UNMATCHED' | 'MATCHED';

const formatDate = (d: string | null | undefined) => {
  if (!d) return '—';
  const dt = new Date(d.includes('T') ? d : d + 'T00:00:00');
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const errMsg = (err: any, fallback: string) => err?.response?.data?.message || err?.message || fallback;

const STATUS_META: Record<string, { label: string; cls: string }> = {
  AUTO_MATCHED: { label: 'Auto matched', cls: 'bg-green-100 text-green-800 border border-green-200' },
  MANUAL_MATCHED: { label: 'Matched', cls: 'bg-green-100 text-green-800 border border-green-200' },
  SUGGESTED: { label: 'Review', cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
  UNMATCHED: { label: 'Bank only', cls: 'bg-rose-50 text-rose-700 border border-rose-200' },
  BANK_ERROR: { label: 'Bank error', cls: 'bg-purple-100 text-purple-800 border border-purple-200' },
  IGNORED: { label: 'Ignored', cls: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

const ADJUSTMENTS: { value: AdjustmentType; label: string; side: 'out' | 'in' | 'both'; hint: string }[] = [
  { value: 'BANK_CHARGES', label: 'Bank charges / auto-debit', side: 'out', hint: 'Dr Bank Charges · Cr Bank' },
  { value: 'DIRECT_PAYMENT', label: 'Direct payment to vendor (ECS / NEFT)', side: 'out', hint: 'Dr Vendor · Cr Bank' },
  { value: 'BANK_INTEREST', label: 'Interest credited by bank', side: 'in', hint: 'Dr Bank · Cr Interest Income' },
  { value: 'DIRECT_RECEIPT', label: 'Direct receipt from customer (NEFT / RTGS)', side: 'in', hint: 'Dr Bank · Cr Customer' },
  { value: 'OTHER', label: 'Other — choose a ledger', side: 'both', hint: 'Dr / Cr the chosen ledger' },
];

const monthBounds = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { start: `${ym}-01`, end: `${ym}-${String(last).padStart(2, '0')}` };
};

// ── Statement file parsing (CSV / Excel) ─────────────────────────────────────

const splitCsvLine = (line: string, delim: string): string[] => {
  const res: string[] = [];
  let cur = '';
  let inQuote = false;
  for (const char of line) {
    if (char === '"') inQuote = !inQuote;
    else if (char === delim && !inQuote) {
      res.push(cur.trim());
      cur = '';
    } else cur += char;
  }
  res.push(cur.trim());
  return res.map((c) => c.replace(/^["']|["']$/g, ''));
};

/** Turn a grid of cells (first rows may be bank letterhead) into statement rows. */
function gridToStatementRows(grid: (string | number | Date | null)[][]): StatementImportRow[] {
  const text = (v: unknown) => (v === null || v === undefined ? '' : String(v)).trim();
  let headerIdx = grid.findIndex((row, i) => {
    if (i > 25) return false;
    const line = row.map(text).join(' ').toLowerCase();
    return line.includes('date') && /(narration|description|particular|debit|credit|withdrawal|deposit|amount)/.test(line);
  });
  if (headerIdx < 0) headerIdx = 0;
  const headers = grid[headerIdx].map((h) => text(h).toLowerCase());
  const find = (names: string[]) => headers.findIndex((h) => names.some((n) => h.includes(n)));
  const dateIdx = find(['txn date', 'transaction date', 'tran date', 'posting date', 'date']);
  const descIdx = find(['narration', 'description', 'particulars', 'remarks', 'details']);
  const debitIdx = find(['withdrawal', 'debit', 'dr amount', 'dr.']);
  const creditIdx = find(['deposit', 'credit', 'cr amount', 'cr.']);
  const balIdx = find(['balance']);
  const refIdx = find(['chq', 'cheque', 'ref', 'utr', 'instrument']);
  const amount = (v: unknown) => {
    if (typeof v === 'number') return Math.abs(v);
    return Math.abs(parseFloat(text(v).replace(/[^\d.-]/g, '')) || 0);
  };
  const dateCell = (v: unknown) => {
    if (v instanceof Date) {
      return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
    }
    return typeof v === 'number' ? v : text(v);
  };
  return grid
    .slice(headerIdx + 1)
    .map((cols) => ({
      date: dateCell(cols[dateIdx >= 0 ? dateIdx : 0]),
      description: text(cols[descIdx >= 0 ? descIdx : 1]),
      debit: amount(cols[debitIdx >= 0 ? debitIdx : 2]),
      credit: amount(cols[creditIdx >= 0 ? creditIdx : 3]),
      balance: balIdx >= 0 && text(cols[balIdx]) !== '' ? amount(cols[balIdx]) : null,
      referenceNo: refIdx >= 0 ? text(cols[refIdx]) : '',
    }))
    .filter((r) => r.date !== '' && (r.debit > 0 || r.credit > 0));
}

export default function BankReconciliationPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [tab, setTab] = useState<TabKey>('reconciliation');
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedBankId, setSelectedBankId] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [workspace, setWorkspace] = useState<ReconciliationWorkspace | null>(null);
  const [historyList, setHistoryList] = useState<ReconciliationHistoryItem[]>([]);
  const [coaAccounts, setCoaAccounts] = useState<CoaAccount[]>([]);
  const [parties, setParties] = useState<PartyResponse[]>([]);

  const [selectedBsId, setSelectedBsId] = useState<string | null>(null);
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [statementFilter, setStatementFilter] = useState<StatementFilter>('ALL');
  const [bookFilter, setBookFilter] = useState<BookFilter>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState<'file' | 'manual'>('file');
  const [filePreview, setFilePreview] = useState<StatementImportRow[]>([]);
  const [manualEntry, setManualEntry] = useState({ date: '', description: '', debit: 0, credit: 0, referenceNo: '' });
  const [manualEntries, setManualEntries] = useState<StatementImportRow[]>([]);
  const [importing, setImporting] = useState(false);

  const [adjustFor, setAdjustFor] = useState<BankStatementEntry | null>(null);
  const [adjustType, setAdjustType] = useState<AdjustmentType>('BANK_CHARGES');
  const [adjustAccountId, setAdjustAccountId] = useState('');
  const [adjustPartyId, setAdjustPartyId] = useState('');
  const [adjustNarration, setAdjustNarration] = useState('');
  const [postingAdjustment, setPostingAdjustment] = useState(false);

  const [balanceEditing, setBalanceEditing] = useState(false);
  const [balanceInput, setBalanceInput] = useState('');

  const [reportOpen, setReportOpen] = useState(false);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [locking, setLocking] = useState(false);
  const [confirm, setConfirm] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  const selectedBank = useMemo(
    () => bankAccounts.find((b) => b.id === selectedBankId) || null,
    [bankAccounts, selectedBankId]
  );

  // ── Load bank accounts, ledgers and parties ──
  useEffect(() => {
    (async () => {
      try {
        setLoadingAccounts(true);
        const [accounts, coaList, partyRes] = await Promise.all([
          bankReconApi.getBankAccounts(),
          financeApi.getAccounts({ isActive: true }).catch(() => [] as CoaAccount[]),
          getAllParties().catch(() => ({ success: false, data: [] as PartyResponse[] })),
        ]);
        setBankAccounts(accounts || []);
        setSelectedBankId((prev) => prev || accounts.find((a) => a.isPrimary)?.id || accounts[0]?.id || '');
        setCoaAccounts(coaList || []);
        setParties(partyRes.data || []);
      } catch (err) {
        toast.error(errMsg(err, 'Failed to load bank accounts'));
      } finally {
        setLoadingAccounts(false);
      }
    })();
  }, [toast]);

  const fetchWorkspace = useCallback(async () => {
    if (!selectedBankId) return;
    try {
      setLoading(true);
      const { start, end } = monthBounds(selectedMonth);
      const header = await bankReconApi.startReconciliation({ bankAccountId: selectedBankId, periodStart: start, periodEnd: end });
      setWorkspace(await bankReconApi.getWorkspace(header.id));
      setSelectedBsId(null);
      setSelectedBookId(null);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to load reconciliation'));
    } finally {
      setLoading(false);
    }
  }, [selectedBankId, selectedMonth, toast]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  useEffect(() => {
    if (tab !== 'history' || !selectedBankId) return;
    bankReconApi.getHistory(selectedBankId).then(setHistoryList).catch(() => setHistoryList([]));
  }, [tab, selectedBankId, workspace?.reconciliation.status]);

  const summary = workspace?.reconciliation;
  const startInfo = workspace?.startInfo || null;
  const isLocked = summary?.status === 'LOCKED';
  const isZeroDifference = summary ? Math.abs(summary.difference) < 0.01 : false;
  const reconId = summary?.id;

  /** Run an action, toast its message, then reload the workspace. */
  const act = async (fn: () => Promise<{ message?: string } | void>, fallback: string) => {
    try {
      setLoading(true);
      const res = await fn();
      if (res && 'message' in res && res.message) toast.success(res.message);
      await fetchWorkspace();
    } catch (err) {
      toast.error(errMsg(err, fallback));
      setLoading(false);
    }
  };

  const handleAutoMatch = () => reconId && act(() => bankReconApi.autoMatch(reconId), 'Auto match failed');
  const handleManualMatch = () => {
    if (!reconId || !selectedBsId || !selectedBookId) {
      toast.error('Select one bank statement line and one book entry to match.');
      return;
    }
    act(() => bankReconApi.manualMatch(reconId, { statementEntryId: selectedBsId, bookEntryId: selectedBookId }), 'Manual match failed');
  };
  const handleUnmatch = (s: BankStatementEntry) => {
    if (!reconId) return;
    const run = () => act(() => bankReconApi.unmatch(reconId, s.id), 'Unmatch failed');
    if (s.adjustmentType) {
      setConfirm({
        title: 'Undo Adjusting Entry',
        message: 'This line was resolved with an adjusting entry. Unmatching removes that entry from the books. Continue?',
        onConfirm: () => {
          setConfirm(null);
          run();
        },
      });
    } else run();
  };
  const handleStatus = (s: BankStatementEntry, status: 'UNMATCHED' | 'BANK_ERROR' | 'IGNORED') =>
    act(() => bankReconApi.updateStatementStatus(s.id, { status }), 'Failed to update the line');
  const handleDelete = (s: BankStatementEntry) =>
    setConfirm({
      title: 'Delete Statement Line',
      message: `Delete "${s.description}" (${formatINR(s.debit || s.credit)})? Use this only for lines imported by mistake.`,
      onConfirm: () => {
        setConfirm(null);
        act(() => bankReconApi.deleteStatementEntry(s.id), 'Failed to delete the line');
      },
    });

  const saveStatementBalance = async () => {
    const value = parseFloat(balanceInput);
    if (!reconId || !Number.isFinite(value)) {
      toast.error('Enter the closing balance shown on the bank statement.');
      return;
    }
    setBalanceEditing(false);
    await act(() => bankReconApi.setStatementBalance(reconId, value), 'Failed to update statement balance');
  };

  // ── Adjusting entry ──
  const openAdjustment = (stmt: BankStatementEntry) => {
    const inflow = stmt.credit > 0;
    setAdjustFor(stmt);
    setAdjustType(inflow ? 'BANK_INTEREST' : 'BANK_CHARGES');
    setAdjustAccountId('');
    setAdjustPartyId('');
    setAdjustNarration(stmt.description);
  };

  const postAdjustment = async () => {
    if (!adjustFor) return;
    if ((adjustType === 'DIRECT_RECEIPT' || adjustType === 'DIRECT_PAYMENT') && !adjustPartyId) {
      toast.error(adjustType === 'DIRECT_RECEIPT' ? 'Select the customer who paid' : 'Select the vendor who was paid');
      return;
    }
    if (adjustType === 'OTHER' && !adjustAccountId) {
      toast.error('Select the ledger for the other side of the entry');
      return;
    }
    try {
      setPostingAdjustment(true);
      const res = await bankReconApi.createAdjustment(adjustFor.id, {
        adjustmentType: adjustType,
        accountId: adjustAccountId || undefined,
        partyId: adjustPartyId || undefined,
        narration: adjustNarration,
      });
      toast.success(res.message);
      setAdjustFor(null);
      await fetchWorkspace();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to post adjusting entry'));
    } finally {
      setPostingAdjustment(false);
    }
  };

  // ── Import ──
  const downloadSampleCsv = () => {
    const csv =
      'Date,Description,Reference,Debit,Credit,Balance\r\n' +
      '05-09-2026,NEFT IN ALPHA CORP,UTR100234,,50000,250000\r\n' +
      '12-09-2026,CHQ PAID APEX TOOLS,000882,20000,,230000\r\n' +
      '18-09-2026,SMS + AMC CHARGES,,450,,229550\r\n' +
      '28-09-2026,INTEREST CREDIT,,,850,230400\r\n';
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bank_statement_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (file: File) => {
    try {
      let grid: (string | number | Date | null)[][];
      if (/\.(xlsx|xls)$/i.test(file.name)) {
        const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
        grid = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: true, defval: null }) as any;
      } else {
        const lines = (await file.text()).split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        const delim = lines[0]?.includes(';') ? ';' : lines[0]?.includes('\t') ? '\t' : ',';
        grid = lines.map((l) => splitCsvLine(l, delim));
      }
      const rows = gridToStatementRows(grid);
      setFilePreview(rows);
      if (rows.length === 0) toast.error('No transactions found. Check the column headers or use the template.');
      else toast.success(`Read ${rows.length} transactions from ${file.name}`);
    } catch (err) {
      toast.error(errMsg(err, 'Could not read the statement file'));
    }
  };

  const handleImportSubmit = async () => {
    if (!reconId || !selectedBankId) return;
    const rows = importMode === 'file' ? filePreview : manualEntries;
    if (rows.length === 0) {
      toast.error('No transactions to import.');
      return;
    }
    try {
      setImporting(true);
      const res = await bankReconApi.importStatement(reconId, { bankAccountId: selectedBankId, rows });
      const { importedCount, skippedDuplicates, rejected } = res.data;
      toast.success(
        `Imported ${importedCount} transaction${importedCount === 1 ? '' : 's'}` +
          (skippedDuplicates ? `, ${skippedDuplicates} duplicates skipped` : '') +
          (rejected.length ? `, ${rejected.length} rejected` : '')
      );
      if (rejected.length) toast.error(rejected.slice(0, 3).map((r) => (r.row ? `Row ${r.row}: ${r.reason}` : r.reason)).join(' · '));
      setImportOpen(false);
      setFilePreview([]);
      setManualEntries([]);
      await fetchWorkspace();
    } catch (err) {
      toast.error(errMsg(err, 'Statement import failed'));
    } finally {
      setImporting(false);
    }
  };

  const handleAddManualEntry = () => {
    if (!manualEntry.date || !manualEntry.description) {
      toast.error('Date and description are required');
      return;
    }
    if ((manualEntry.debit > 0) === (manualEntry.credit > 0)) {
      toast.error('Enter either a withdrawal or a deposit amount');
      return;
    }
    setManualEntries((p) => [...p, { ...manualEntry, balance: null }]);
    setManualEntry({ date: manualEntry.date, description: '', debit: 0, credit: 0, referenceNo: '' });
  };

  const handleLock = async () => {
    if (!reconId) return;
    try {
      setLocking(true);
      const res = await bankReconApi.lockReconciliation(reconId, { overrideReason: overrideReason.trim() || undefined });
      toast.success(res.message);
      setLockModalOpen(false);
      setOverrideReason('');
      await fetchWorkspace();
    } catch (err) {
      toast.error(errMsg(err, 'Locking failed'));
    } finally {
      setLocking(false);
    }
  };

  // ── Derived views ──
  const q = searchQuery.trim().toLowerCase();
  const filteredStatements = useMemo(() => {
    return (workspace?.statementEntries || []).filter((s) => {
      if (statementFilter === 'OPEN' && s.matchStatus !== 'UNMATCHED') return false;
      if (statementFilter === 'SUGGESTED' && s.matchStatus !== 'SUGGESTED') return false;
      if (statementFilter === 'MATCHED' && !s.matchStatus.endsWith('MATCHED')) return false;
      if (statementFilter === 'EXCLUDED' && !['BANK_ERROR', 'IGNORED'].includes(s.matchStatus)) return false;
      if (!q) return true;
      return [s.description, s.referenceNo, s.matchedJvNumber, String(s.debit || s.credit)].some((v) => v && v.toLowerCase().includes(q));
    });
  }, [workspace?.statementEntries, statementFilter, q]);

  const filteredBookEntries = useMemo(() => {
    return (workspace?.bookEntries || []).filter((b) => {
      if (bookFilter === 'UNMATCHED' && b.isMatched) return false;
      if (bookFilter === 'MATCHED' && !b.isMatched) return false;
      if (!q) return true;
      return [b.voucherNumber, b.narration, b.partyName, b.chequeNo, b.referenceNo, String(b.debit || b.credit)].some(
        (v) => v && v.toLowerCase().includes(q)
      );
    });
  }, [workspace?.bookEntries, bookFilter, q]);

  const unmatchedBooks = (workspace?.bookEntries || []).filter((b) => !b.isMatched);
  const outstandingCheques = unmatchedBooks.filter((b) => b.credit > 0);
  const depositsInTransit = unmatchedBooks.filter((b) => b.debit > 0);
  const bankOnly = (workspace?.statementEntries || []).filter((s) => s.matchStatus === 'UNMATCHED' || s.matchStatus === 'SUGGESTED');
  const bankOnlyCredits = bankOnly.filter((s) => s.credit > 0);
  const bankOnlyDebits = bankOnly.filter((s) => s.debit > 0);
  const bankErrors = (workspace?.statementEntries || []).filter((s) => s.matchStatus === 'BANK_ERROR');

  const exportCSV = () => {
    if (!workspace) return;
    const rows = workspace.statementEntries.map((s) =>
      [s.date, `"${s.description.replace(/"/g, '""')}"`, s.referenceNo || '', s.debit, s.credit, s.balance ?? '', s.matchStatus, s.matchedJvNumber || ''].join(',')
    );
    const csv = ['Date,Description,Reference,Debit,Credit,Balance,Status,Matched Voucher', ...rows].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `BankRecon_${selectedBank?.accountName || 'account'}_${selectedMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const adjustmentOptions = adjustFor
    ? ADJUSTMENTS.filter((o) => o.side === 'both' || o.side === (adjustFor.credit > 0 ? 'in' : 'out'))
    : [];
  const partyOptions = parties.filter((p) =>
    adjustType === 'DIRECT_RECEIPT' ? p.type !== 'Supplier' : adjustType === 'DIRECT_PAYMENT' ? p.type !== 'Customer' : false
  );
  const ledgerOptions = coaAccounts.filter((a) => a.id !== selectedBank?.ledgerAccountId && a.allowDirectPosting);

  // ── No bank account configured ──
  if (!loadingAccounts && bankAccounts.length === 0) {
    return (
      <AppLayout>
        <div className="p-6">
          <h1 className="text-xl font-bold text-[#1e293b]">Bank Reconciliation</h1>
          <p className="text-sm text-slate-500 mt-0.5">Reconcile bank statements with the General Ledger cash book</p>
          <div className="mt-6 bg-white border border-[#e2e8f0] rounded-xl py-16 text-center">
            <i className="ri-bank-line text-4xl text-slate-300" />
            <p className="text-sm font-semibold text-[#1e293b] mt-2">No bank account added yet</p>
            <p className="text-xs text-slate-500 mt-1">Add your company bank accounts in Settings → Company → Bank Accounts.</p>
            <button
              onClick={() => navigate('/settings')}
              className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer"
            >
              <i className="ri-settings-3-line" /> Go to Settings
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap px-6 pt-6">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-[#1e293b]">Bank Reconciliation</h1>
              {summary && (
                <span
                  className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                    summary.status === 'LOCKED'
                      ? 'bg-purple-100 text-purple-800'
                      : summary.status === 'RECONCILED'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {summary.status === 'LOCKED' ? 'Locked' : summary.status === 'RECONCILED' ? 'Reconciled' : 'Open'}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">Match the bank statement with the cash book, explain every difference, then lock the month</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedBankId}
              onChange={(e) => setSelectedBankId(e.target.value)}
              disabled={loadingAccounts}
              className="h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] cursor-pointer min-w-[240px] font-medium"
            >
              {bankAccounts.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.accountName} ({b.accountNumber.slice(-4)}){b.isPrimary ? ' · Primary' : ''}
                </option>
              ))}
            </select>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] cursor-pointer font-medium"
            />
            {!isLocked && (
              <button
                onClick={() => setImportOpen(true)}
                className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer"
              >
                <i className="ri-upload-2-line text-primary-600" /> Import Statement
              </button>
            )}
            <button
              onClick={() => setReportOpen(true)}
              className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer"
            >
              <i className="ri-file-chart-line text-primary-600" /> BRS Statement
            </button>
          </div>
        </div>

        {/* Bank account card */}
        {selectedBank && (
          <div className="px-6 pt-5">
            <div className="bg-white border border-[#e2e8f0] rounded-xl px-4 py-2.5 flex items-center justify-between flex-wrap gap-3 text-xs">
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <span className="text-slate-500">Bank: </span>
                  <span className="font-semibold text-[#1e293b]">{selectedBank.bankName}</span>
                </div>
                <div>
                  <span className="text-slate-500">A/c No: </span>
                  <span className="font-mono font-semibold text-[#1e293b]">{selectedBank.accountNumber}</span>
                </div>
                <div>
                  <span className="text-slate-500">IFSC: </span>
                  <span className="font-mono text-slate-700">{selectedBank.ifscCode || '—'}</span>
                </div>
                {selectedBank.branchName && (
                  <div>
                    <span className="text-slate-500">Branch: </span>
                    <span className="text-slate-700">{selectedBank.branchName}</span>
                  </div>
                )}
                <div>
                  <span className="text-slate-500">Ledger: </span>
                  <span className="text-primary-600 font-semibold">
                    {selectedBank.ledgerCode} — {selectedBank.ledgerName}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span>
                  <span className="text-slate-500">Last reconciled: </span>
                  <span className="font-semibold text-slate-800">
                    {selectedBank.lastReconciledDate ? formatDate(selectedBank.lastReconciledDate) : 'Never'}
                  </span>
                </span>
                <button onClick={() => navigate('/settings')} className="text-primary-600 hover:underline font-medium cursor-pointer">
                  Manage in Settings
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="px-6 pt-5">
          <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit border border-[#e2e8f0]">
            {([
              { key: 'reconciliation', label: 'Matching Workspace', icon: 'ri-scales-3-line' },
              { key: 'cheques', label: `Timing Differences (${outstandingCheques.length + depositsInTransit.length})`, icon: 'ri-time-line' },
              { key: 'history', label: 'Past Reconciliations', icon: 'ri-history-line' },
            ] as { key: TabKey; label: string; icon: string }[]).map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                  tab === t.key ? 'bg-white text-[#1e293b] shadow-sm' : 'text-slate-600 hover:text-[#1e293b]'
                }`}
              >
                <i className={`${t.icon} text-sm`} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── WORKSPACE ── */}
        {tab === 'reconciliation' && (
          <div className="flex-1 overflow-y-auto px-6 pt-5 pb-6 space-y-5">
            {/* Balances */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Cash Book Balance</p>
                <p className="text-xl font-bold text-[#1e293b] mt-1">{formatINR(summary?.closingBalanceBook || 0)}</p>
                <p className="text-[11px] text-slate-400 mt-1">General Ledger on {formatDate(summary?.periodEnd)}</p>
              </div>
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Bank Statement Balance</p>
                  {!isLocked && !balanceEditing && (
                    <button
                      onClick={() => {
                        setBalanceInput(String(summary?.closingBalanceBank ?? ''));
                        setBalanceEditing(true);
                      }}
                      className="text-slate-400 hover:text-primary-600 cursor-pointer"
                      title="Enter the closing balance shown on the statement"
                    >
                      <i className="ri-pencil-line text-sm" />
                    </button>
                  )}
                </div>
                {balanceEditing ? (
                  <div className="flex items-center gap-1.5 mt-1">
                    <input
                      type="number"
                      autoFocus
                      value={balanceInput}
                      onChange={(e) => setBalanceInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && saveStatementBalance()}
                      className="w-full h-8 px-2 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                    />
                    <button onClick={saveStatementBalance} className="h-8 px-2.5 rounded-lg bg-[#4f46e5] text-white text-xs font-medium cursor-pointer">
                      Save
                    </button>
                  </div>
                ) : (
                  <p className="text-xl font-bold text-primary-600 mt-1">{formatINR(summary?.closingBalanceBank || 0)}</p>
                )}
                <p className="text-[11px] text-slate-400 mt-1">Closing balance as per statement</p>
              </div>
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Adjusted Bank / Books</p>
                <p className="text-sm font-bold text-[#1e293b] mt-1.5">
                  {formatINR(summary?.adjustedBankBalance || 0)} <span className="text-slate-400 font-normal">vs</span>{' '}
                  {formatINR(summary?.adjustedBookBalance || 0)}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">After timing differences & bank-only items</p>
              </div>
              <div className={`border rounded-xl p-5 ${isZeroDifference ? 'bg-green-50/70 border-green-300' : 'bg-red-50/70 border-red-300'}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Difference</p>
                  <i className={`text-lg ${isZeroDifference ? 'ri-checkbox-circle-fill text-green-600' : 'ri-error-warning-fill text-red-500'}`} />
                </div>
                <p className={`text-xl font-bold mt-1 ${isZeroDifference ? 'text-green-700' : 'text-red-700'}`}>{formatINR(summary?.difference || 0)}</p>
                <p className={`text-[11px] font-semibold mt-1 ${isZeroDifference ? 'text-green-800' : 'text-red-800'}`}>
                  {isZeroDifference ? 'Books = Bank ✅' : 'Investigate before locking'}
                </p>
              </div>
            </div>

            {/* Matching buckets */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Matched', value: summary?.totalMatched || 0, icon: 'ri-checkbox-circle-line', color: 'bg-emerald-50 text-emerald-600', filter: () => setStatementFilter('MATCHED') },
                { label: 'Needs Review', value: summary?.totalSuggested || 0, icon: 'ri-question-line', color: 'bg-amber-50 text-amber-600', filter: () => setStatementFilter('SUGGESTED') },
                { label: 'Bank Only', value: (summary?.totalUnmatchedBank || 0) - (summary?.totalSuggested || 0), icon: 'ri-bank-line', color: 'bg-rose-50 text-rose-600', filter: () => setStatementFilter('OPEN') },
                { label: 'Books Only', value: summary?.totalUnmatchedBook || 0, icon: 'ri-book-open-line', color: 'bg-sky-50 text-sky-600', filter: () => setBookFilter('UNMATCHED') },
              ].map((c) => (
                <button key={c.label} onClick={c.filter} className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3 text-left hover:border-indigo-200 cursor-pointer">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${c.color}`}>
                    <i className={`${c.icon} text-lg`} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{c.label}</p>
                    <p className="text-xl font-bold text-[#1e293b]">{c.value}</p>
                  </div>
                </button>
              ))}
            </div>

            {startInfo && Math.abs(startInfo.openingDifference) >= 0.01 && (
              <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-xl px-4 py-3 text-xs flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2">
                  <i className="ri-error-warning-line text-amber-600 text-base mt-0.5" />
                  <span>
                    <b>Opening balance mismatch.</b> The statement balance you entered for {formatDate(startInfo.startDate)} is{' '}
                    <b>{formatINR(startInfo.statementOpeningBalance)}</b>, but the cash book on that date shows{' '}
                    <b>{formatINR(startInfo.bookBalanceAtStart)}</b>. The difference of <b>{formatINR(startInfo.openingDifference)}</b> will remain
                    in every BRS until corrected — enter the bank's actual balance on that date, or choose a start date when both agreed.
                  </span>
                </div>
                <button onClick={() => navigate('/settings')} className="h-8 px-3 rounded-lg bg-white border border-amber-300 text-amber-900 font-medium hover:bg-amber-100 cursor-pointer whitespace-nowrap">
                  Fix in Settings
                </button>
              </div>
            )}

            {(summary?.staleChequesCount || 0) > 0 && (
              <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-xl px-4 py-2.5 text-xs flex items-center gap-2">
                <i className="ri-alarm-warning-line text-amber-600 text-base" />
                <span>
                  <b>{summary?.staleChequesCount} stale cheque(s)</b> worth {formatINR(summary?.staleChequesAmount || 0)} are more than 6 months old and still
                  not presented. Cancel those payments in Purchase → Payments so they reverse in the books.
                </span>
              </div>
            )}

            {/* Toolbar */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-3 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search narration, cheque / UTR, amount..."
                    className="h-9 pl-8 pr-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] w-72"
                  />
                </div>
                {!isLocked && (
                  <>
                    <button
                      onClick={handleAutoMatch}
                      disabled={loading}
                      className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer disabled:opacity-50"
                      title="Same amount + date within ±2 days + same reference → matched; partial → review"
                    >
                      <i className="ri-magic-line" /> Run Auto-Match
                    </button>
                    <button
                      onClick={handleManualMatch}
                      disabled={!selectedBsId || !selectedBookId || loading}
                      className={`flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium ${
                        selectedBsId && selectedBookId ? 'bg-green-600 text-white hover:bg-green-700 cursor-pointer' : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      <i className="ri-link" /> Match Selected
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={exportCSV}
                  className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer"
                >
                  <i className="ri-download-2-line" /> Export CSV
                </button>
                {!isLocked && (
                  <button
                    onClick={() => setLockModalOpen(true)}
                    className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 cursor-pointer"
                  >
                    <i className="ri-lock-line" /> Lock Period
                  </button>
                )}
              </div>
            </div>

            {/* Statement vs Books */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {/* Bank statement */}
              <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden flex flex-col h-[560px]">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <i className="ri-bank-line text-primary-600" /> Bank Statement
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">{filteredStatements.length}</span>
                  </p>
                  <div className="flex items-center gap-1">
                    {([
                      ['ALL', 'All'],
                      ['OPEN', 'Bank only'],
                      ['SUGGESTED', 'Review'],
                      ['MATCHED', 'Matched'],
                      ['EXCLUDED', 'Errors / Ignored'],
                    ] as [StatementFilter, string][]).map(([f, label]) => (
                      <button
                        key={f}
                        onClick={() => setStatementFilter(f)}
                        className={`px-2 py-1 rounded text-[10px] font-semibold cursor-pointer ${
                          statementFilter === f ? 'bg-[#4f46e5] text-white' : 'text-slate-500 hover:bg-slate-200/70'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex-1 overflow-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold sticky top-0 z-10 border-b border-slate-100">
                      <tr>
                        <th className="px-3 py-2 w-8"></th>
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Description</th>
                        <th className="px-3 py-2 text-right">Withdrawal</th>
                        <th className="px-3 py-2 text-right">Deposit</th>
                        <th className="px-3 py-2 text-center">Status</th>
                        <th className="px-3 py-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredStatements.map((s) => {
                        const isSelected = selectedBsId === s.id;
                        const matched = s.matchStatus.endsWith('MATCHED');
                        const selectable = !isLocked && (s.matchStatus === 'UNMATCHED' || s.matchStatus === 'SUGGESTED');
                        const meta = STATUS_META[s.matchStatus] || STATUS_META.UNMATCHED;
                        return (
                          <tr
                            key={s.id}
                            onClick={() => selectable && setSelectedBsId(isSelected ? null : s.id)}
                            className={`transition-colors ${
                              isSelected ? 'bg-primary-50 border-l-4 border-primary-600' : matched ? 'bg-green-50/30' : selectable ? 'hover:bg-slate-50 cursor-pointer' : ''
                            }`}
                          >
                            <td className="px-3 py-2 text-center">
                              {selectable && <input type="radio" name="stmt" checked={isSelected} onChange={() => setSelectedBsId(s.id)} className="cursor-pointer" />}
                            </td>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                              {formatDate(s.date)}
                              {s.isCarriedForward && <p className="text-[9px] text-amber-600 font-semibold">Brought forward</p>}
                            </td>
                            <td className="px-3 py-2">
                              <p className="font-medium text-[#1e293b] line-clamp-1">{s.description}</p>
                              {s.referenceNo && <p className="text-[10px] text-slate-500 font-mono">Ref: {s.referenceNo}</p>}
                              {s.matchedJvNumber && (
                                <p className="text-[10px] text-green-700 font-semibold">
                                  ↔ {s.matchedJvNumber}
                                  {s.adjustmentType ? ` · ${ADJUSTMENTS.find((a) => a.value === s.adjustmentType)?.label}${s.adjustmentPartyName ? ` (${s.adjustmentPartyName})` : ''}` : ''}
                                </p>
                              )}
                              {s.matchStatus === 'SUGGESTED' && (
                                <p className="text-[10px] text-amber-700 font-semibold">
                                  Suggested: {s.suggestedJvNumber} · {formatDate(s.suggestedDate)}
                                  {s.suggestedParty ? ` · ${s.suggestedParty}` : ''}
                                </p>
                              )}
                              {s.notes && <p className="text-[10px] text-slate-400 italic line-clamp-1">{s.notes}</p>}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-red-600 whitespace-nowrap">{s.debit > 0 ? formatINR(s.debit) : '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold text-green-600 whitespace-nowrap">{s.credit > 0 ? formatINR(s.credit) : '—'}</td>
                            <td className="px-3 py-2 text-center whitespace-nowrap">
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                              {!isLocked && (
                                <div className="flex items-center justify-center gap-1">
                                  {s.matchStatus === 'SUGGESTED' && (
                                    <>
                                      <button
                                        onClick={() => reconId && act(() => bankReconApi.acceptSuggestion(reconId, s.id), 'Failed to confirm match')}
                                        className="px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 font-semibold text-[10px] cursor-pointer"
                                      >
                                        Accept
                                      </button>
                                      <button
                                        onClick={() => reconId && act(() => bankReconApi.rejectSuggestion(reconId, s.id), 'Failed to reject')}
                                        className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 cursor-pointer"
                                        title="Reject suggestion"
                                      >
                                        <i className="ri-close-line" />
                                      </button>
                                    </>
                                  )}
                                  {s.matchStatus === 'UNMATCHED' && (
                                    <>
                                      <button
                                        onClick={() => openAdjustment(s)}
                                        className="px-2 py-1 rounded bg-primary-50 text-primary-700 hover:bg-primary-100 font-semibold text-[10px] cursor-pointer"
                                        title="Pass the adjusting entry in the books"
                                      >
                                        Resolve
                                      </button>
                                      <button onClick={() => handleStatus(s, 'BANK_ERROR')} className="p-1 rounded text-slate-400 hover:text-purple-600 hover:bg-purple-50 cursor-pointer" title="Mark as bank error">
                                        <i className="ri-error-warning-line" />
                                      </button>
                                      <button onClick={() => handleStatus(s, 'IGNORED')} className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer" title="Ignore (e.g. duplicate line)">
                                        <i className="ri-eye-off-line" />
                                      </button>
                                      <button onClick={() => handleDelete(s)} className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 cursor-pointer" title="Delete line imported by mistake">
                                        <i className="ri-delete-bin-line" />
                                      </button>
                                    </>
                                  )}
                                  {matched && (
                                    <button onClick={() => handleUnmatch(s)} className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 cursor-pointer" title="Unmatch">
                                      <i className="ri-link-unlink-m" />
                                    </button>
                                  )}
                                  {(s.matchStatus === 'BANK_ERROR' || s.matchStatus === 'IGNORED') && (
                                    <button
                                      onClick={() => handleStatus(s, 'UNMATCHED')}
                                      className="px-2 py-1 rounded text-slate-600 hover:bg-slate-100 text-[10px] font-semibold cursor-pointer"
                                    >
                                      Restore
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {filteredStatements.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center py-12 text-slate-400">
                            <i className="ri-inbox-line text-3xl block mb-1 text-slate-300" />
                            {(workspace?.statementEntries.length || 0) === 0 ? 'Import the bank statement for this month to start.' : 'No lines for this filter.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Books */}
              <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden flex flex-col h-[560px]">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <i className="ri-book-read-line text-primary-600" /> Cash Book (General Ledger)
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">{filteredBookEntries.length}</span>
                  </p>
                  <div className="flex items-center gap-1">
                    {([
                      ['ALL', 'All'],
                      ['UNMATCHED', 'Books only'],
                      ['MATCHED', 'Matched'],
                    ] as [BookFilter, string][]).map(([f, label]) => (
                      <button
                        key={f}
                        onClick={() => setBookFilter(f)}
                        className={`px-2 py-1 rounded text-[10px] font-semibold cursor-pointer ${bookFilter === f ? 'bg-[#4f46e5] text-white' : 'text-slate-500 hover:bg-slate-200/70'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex-1 overflow-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold sticky top-0 z-10 border-b border-slate-100">
                      <tr>
                        <th className="px-3 py-2 w-8"></th>
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Voucher / Party</th>
                        <th className="px-3 py-2 text-right">Receipt (Dr)</th>
                        <th className="px-3 py-2 text-right">Payment (Cr)</th>
                        <th className="px-3 py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredBookEntries.map((b) => {
                        const isSelected = selectedBookId === b.id;
                        const selectable = !isLocked && !b.isMatched;
                        return (
                          <tr
                            key={b.id}
                            onClick={() => selectable && setSelectedBookId(isSelected ? null : b.id)}
                            className={`transition-colors ${
                              isSelected ? 'bg-primary-50 border-l-4 border-primary-600' : b.isMatched ? 'bg-green-50/30' : selectable ? 'hover:bg-slate-50 cursor-pointer' : ''
                            }`}
                          >
                            <td className="px-3 py-2 text-center">
                              {selectable && <input type="radio" name="book" checked={isSelected} onChange={() => setSelectedBookId(b.id)} className="cursor-pointer" />}
                            </td>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                              {formatDate(b.date)}
                              {b.isCarriedForward && !b.isMatched && <p className="text-[9px] text-amber-600 font-semibold">Brought forward</p>}
                            </td>
                            <td className="px-3 py-2">
                              <p className="font-mono font-semibold text-primary-600">{b.voucherNumber}</p>
                              <p className="text-[11px] text-slate-600 line-clamp-1">{b.partyName || b.narration || b.voucherType}</p>
                              {(b.chequeNo || b.referenceNo) && (
                                <p className="text-[10px] text-slate-500 font-mono">
                                  {b.chequeNo ? `Chq ${b.chequeNo}` : ''} {b.referenceNo ? `Ref ${b.referenceNo}` : ''}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-green-600 whitespace-nowrap">{b.debit > 0 ? formatINR(b.debit) : '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold text-red-600 whitespace-nowrap">{b.credit > 0 ? formatINR(b.credit) : '—'}</td>
                            <td className="px-3 py-2 text-center whitespace-nowrap">
                              <span
                                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                  b.isMatched
                                    ? 'bg-green-100 text-green-800 border border-green-200'
                                    : b.isStale
                                    ? 'bg-red-100 text-red-700 border border-red-200'
                                    : b.suggestedStatementId
                                    ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                    : 'bg-sky-100 text-sky-800 border border-sky-200'
                                }`}
                              >
                                {b.isMatched ? 'Matched' : b.isStale ? 'Stale' : b.suggestedStatementId ? 'Review' : 'Books only'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredBookEntries.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center py-12 text-slate-400">
                            <i className="ri-inbox-line text-3xl block mb-1 text-slate-300" />
                            {(workspace?.bookEntries.length || 0) > 0
                              ? 'No book entries for this filter.'
                              : startInfo && startInfo.entriesBeforeStart > 0
                              ? `No receipts or payments through this bank since the statement start date (${formatDate(startInfo.startDate)}). ${startInfo.entriesBeforeStart} earlier ${startInfo.entriesBeforeStart === 1 ? 'entry is' : 'entries are'} treated as already cleared.`
                              : 'No receipts or payments have been posted to this bank ledger up to this month.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── TIMING DIFFERENCES ── */}
        {tab === 'cheques' && (
          <div className="flex-1 overflow-y-auto px-6 pt-5 pb-6 space-y-5">
            {[
              {
                title: 'Outstanding Cheques & Payments',
                subtitle: 'Paid in the books, not yet debited by the bank. No entry needed — they match when the bank clears them.',
                items: outstandingCheques,
                amount: (b: (typeof unmatchedBooks)[number]) => b.credit,
                tone: 'text-red-600',
                icon: 'ri-coupon-3-line text-red-600',
              },
              {
                title: 'Deposits in Transit',
                subtitle: 'Received in the books, not yet credited by the bank.',
                items: depositsInTransit,
                amount: (b: (typeof unmatchedBooks)[number]) => b.debit,
                tone: 'text-green-600',
                icon: 'ri-inbox-archive-line text-green-600',
              },
            ].map((section) => (
              <div key={section.title} className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      <i className={section.icon} /> {section.title}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{section.subtitle}</p>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
                    Total: {formatINR(section.items.reduce((s, b) => s + section.amount(b), 0))}
                  </span>
                </div>
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold border-b border-slate-100">
                    <tr>
                      <th className="px-4 py-2.5">Date</th>
                      <th className="px-4 py-2.5">Voucher</th>
                      <th className="px-4 py-2.5">Party / Narration</th>
                      <th className="px-4 py-2.5">Cheque / Ref</th>
                      <th className="px-4 py-2.5 text-right">Amount</th>
                      <th className="px-4 py-2.5 text-center">Age</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {section.items.map((b) => {
                      const days = summary ? Math.round((new Date(summary.periodEnd).getTime() - new Date(b.date).getTime()) / 86400000) : 0;
                      return (
                        <tr key={b.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 text-slate-700">{formatDate(b.date)}</td>
                          <td className="px-4 py-2.5 font-mono font-semibold text-primary-600">{b.voucherNumber}</td>
                          <td className="px-4 py-2.5 text-[#1e293b]">{b.partyName || b.narration}</td>
                          <td className="px-4 py-2.5 font-mono text-slate-500">{b.chequeNo || b.referenceNo || '—'}</td>
                          <td className={`px-4 py-2.5 text-right font-semibold ${section.tone}`}>{formatINR(section.amount(b))}</td>
                          <td className="px-4 py-2.5 text-center">
                            {b.isStale ? (
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200" title="Older than 6 months — cancel and reverse">
                                Stale · {days}d
                              </span>
                            ) : (
                              <span className="text-[11px] text-slate-500">{days} days</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {section.items.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-slate-400">
                          Nothing pending.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {/* ── HISTORY ── */}
        {tab === 'history' && (
          <div className="flex-1 overflow-y-auto px-6 pt-5 pb-6">
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-x-auto">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
                <p className="text-sm font-semibold text-slate-800">Reconciliation History</p>
                <p className="text-xs text-slate-400">Monthly reconciliations for {selectedBank?.accountName}</p>
              </div>
              <table className="w-full text-left text-xs min-w-[900px]">
                <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-2.5">Period</th>
                    <th className="px-4 py-2.5 text-right">Bank Balance</th>
                    <th className="px-4 py-2.5 text-right">Book Balance</th>
                    <th className="px-4 py-2.5 text-right">Difference</th>
                    <th className="px-4 py-2.5 text-center">Matched</th>
                    <th className="px-4 py-2.5 text-center">Status</th>
                    <th className="px-4 py-2.5">Locked By / At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {historyList.map((h) => (
                    <tr
                      key={h.id}
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => {
                        setSelectedMonth(h.periodStart.slice(0, 7));
                        setTab('reconciliation');
                      }}
                    >
                      <td className="px-4 py-2.5 font-semibold text-[#1e293b] whitespace-nowrap">
                        {formatDate(h.periodStart)} – {formatDate(h.periodEnd)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-primary-600 font-semibold">{formatINR(h.closingBalanceBank)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-[#1e293b] font-semibold">{formatINR(h.closingBalanceBook)}</td>
                      <td className={`px-4 py-2.5 text-right font-mono font-semibold ${Math.abs(h.difference) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatINR(h.difference)}
                      </td>
                      <td className="px-4 py-2.5 text-center font-semibold text-green-700">{h.totalMatched}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            h.status === 'LOCKED' ? 'bg-purple-100 text-purple-800' : h.status === 'RECONCILED' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {h.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-[11px] text-slate-500">
                        {h.lockedAt ? (
                          <>
                            {formatDate(h.lockedAt)}
                            {h.lockedByEmail && <span className="block text-[10px]">{h.lockedByEmail}</span>}
                            {h.overrideReason && <span className="block text-[10px] text-amber-600">Override: {h.overrideReason}</span>}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                  {historyList.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-slate-400">
                        No reconciliations yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── IMPORT MODAL ── */}
        {importOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col border border-[#e2e8f0]">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
                <div>
                  <h2 className="text-base font-bold text-[#1e293b]">Import Bank Statement</h2>
                  <p className="text-xs text-slate-500">
                    {selectedBank?.accountName} · {formatDate(summary?.periodStart)} – {formatDate(summary?.periodEnd)}
                  </p>
                </div>
                <button onClick={() => setImportOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="px-6 pt-4 flex gap-2 border-b border-slate-100">
                {([
                  ['file', 'ri-file-excel-2-line', 'Upload CSV / Excel'],
                  ['manual', 'ri-edit-line', 'Manual Entry'],
                ] as const).map(([mode, icon, label]) => (
                  <button
                    key={mode}
                    onClick={() => setImportMode(mode)}
                    className={`pb-2.5 px-3 text-xs font-semibold cursor-pointer border-b-2 ${
                      importMode === mode ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <i className={`${icon} mr-1`} />
                    {label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {importMode === 'file' ? (
                  <>
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const f = e.dataTransfer.files?.[0];
                        if (f) handleFile(f);
                      }}
                      className="border-2 border-dashed border-indigo-300 rounded-xl p-6 text-center bg-indigo-50/20"
                    >
                      <i className="ri-file-upload-line text-4xl text-indigo-600 mb-2 block" />
                      <p className="text-sm font-semibold text-[#1e293b]">Drop the statement downloaded from net banking</p>
                      <p className="text-xs text-slate-500 mb-4">
                        CSV, XLS or XLSX · columns detected automatically: Date, Narration, Chq/Ref No, Withdrawal, Deposit, Balance
                      </p>
                      <div className="flex items-center justify-center gap-3">
                        <label className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer">
                          <i className="ri-folder-open-line" /> Choose File
                          <input
                            type="file"
                            accept=".csv,.xls,.xlsx,text/csv"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleFile(f);
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={downloadSampleCsv}
                          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg border border-[#e2e8f0] bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium cursor-pointer"
                        >
                          <i className="ri-download-2-line" /> Template
                        </button>
                      </div>
                    </div>
                    {filePreview.length > 0 && (
                      <StatementPreview rows={filePreview} onClear={() => setFilePreview([])} />
                    )}
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-6 gap-2 bg-slate-50 p-3 rounded-xl border border-[#e2e8f0]">
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Date</label>
                        <input
                          type="date"
                          value={manualEntry.date}
                          min={summary?.periodStart}
                          max={summary?.periodEnd}
                          onChange={(e) => setManualEntry((p) => ({ ...p, date: e.target.value }))}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>
                      <div className="col-span-4">
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Narration</label>
                        <input
                          type="text"
                          value={manualEntry.description}
                          onChange={(e) => setManualEntry((p) => ({ ...p, description: e.target.value }))}
                          placeholder="As printed on the statement"
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Chq / UTR No.</label>
                        <input
                          type="text"
                          value={manualEntry.referenceNo}
                          onChange={(e) => setManualEntry((p) => ({ ...p, referenceNo: e.target.value }))}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm font-mono focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Withdrawal</label>
                        <input
                          type="number"
                          value={manualEntry.debit || ''}
                          onChange={(e) => setManualEntry((p) => ({ ...p, debit: Number(e.target.value) }))}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Deposit</label>
                        <input
                          type="number"
                          value={manualEntry.credit || ''}
                          onChange={(e) => setManualEntry((p) => ({ ...p, credit: Number(e.target.value) }))}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>
                      <div className="col-span-2 flex items-end">
                        <button
                          type="button"
                          onClick={handleAddManualEntry}
                          className="w-full h-9 rounded-lg bg-[#4f46e5] text-white font-medium text-sm hover:bg-indigo-700 cursor-pointer"
                        >
                          + Add Line
                        </button>
                      </div>
                    </div>
                    {manualEntries.length > 0 && (
                      <StatementPreview rows={manualEntries} onRemove={(i) => setManualEntries((p) => p.filter((_, idx) => idx !== i))} />
                    )}
                  </>
                )}
              </div>

              <div className="px-6 py-4 border-t border-[#e2e8f0] bg-slate-50 flex items-center justify-end gap-3">
                <button onClick={() => setImportOpen(false)} className="h-9 px-4 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer">
                  Cancel
                </button>
                <button
                  onClick={handleImportSubmit}
                  disabled={importing || (importMode === 'file' ? filePreview.length === 0 : manualEntries.length === 0)}
                  className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? 'Importing…' : `Import ${importMode === 'file' ? filePreview.length : manualEntries.length} Transactions`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── ADJUSTING ENTRY MODAL ── */}
        {adjustFor && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-[#e2e8f0]">
              <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
                <div>
                  <h2 className="text-base font-bold text-[#1e293b]">Resolve Bank-only Line</h2>
                  <p className="text-xs text-slate-500">Pass the adjusting entry in the books — it is matched automatically</p>
                </div>
                <button onClick={() => setAdjustFor(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
              <div className="p-6 space-y-4 text-xs">
                <div className="p-3 rounded-xl bg-slate-50 border border-[#e2e8f0] space-y-1">
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Date</span>
                    <span className="font-semibold text-[#1e293b]">{formatDate(adjustFor.date)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Narration</span>
                    <span className="font-semibold text-[#1e293b] text-right">{adjustFor.description}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Amount</span>
                    <span className={`font-semibold ${adjustFor.debit > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {adjustFor.debit > 0 ? `Withdrawal ${formatINR(adjustFor.debit)}` : `Deposit ${formatINR(adjustFor.credit)}`}
                    </span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500">What is this?</label>
                  {adjustmentOptions.map((o) => (
                    <label
                      key={o.value}
                      className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border cursor-pointer ${
                        adjustType === o.value ? 'border-indigo-300 bg-indigo-50/50' : 'border-[#e2e8f0] hover:bg-slate-50'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="adjust"
                          checked={adjustType === o.value}
                          onChange={() => {
                            setAdjustType(o.value);
                            setAdjustPartyId('');
                            setAdjustAccountId('');
                          }}
                        />
                        <span className="text-sm text-[#1e293b]">{o.label}</span>
                      </span>
                      <span className="text-[10px] font-mono text-slate-400">{o.hint}</span>
                    </label>
                  ))}
                </div>

                {(adjustType === 'DIRECT_RECEIPT' || adjustType === 'DIRECT_PAYMENT') && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">{adjustType === 'DIRECT_RECEIPT' ? 'Customer' : 'Vendor'} *</label>
                    <select
                      value={adjustPartyId}
                      onChange={(e) => setAdjustPartyId(e.target.value)}
                      className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                    >
                      <option value="">Select…</option>
                      {partyOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(adjustType === 'OTHER' || adjustType === 'BANK_CHARGES' || adjustType === 'BANK_INTEREST') && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Ledger {adjustType === 'OTHER' ? '*' : '(optional — default from Posting Rules)'}
                    </label>
                    <select
                      value={adjustAccountId}
                      onChange={(e) => setAdjustAccountId(e.target.value)}
                      className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                    >
                      <option value="">{adjustType === 'OTHER' ? 'Select ledger…' : 'Default account'}</option>
                      {ledgerOptions.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Narration</label>
                  <input
                    type="text"
                    value={adjustNarration}
                    onChange={(e) => setAdjustNarration(e.target.value)}
                    className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-[#e2e8f0] bg-slate-50 flex items-center justify-end gap-3">
                <button onClick={() => setAdjustFor(null)} className="h-9 px-4 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer">
                  Cancel
                </button>
                <button
                  onClick={postAdjustment}
                  disabled={postingAdjustment}
                  className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer disabled:opacity-50"
                >
                  {postingAdjustment ? 'Posting…' : 'Post Entry & Match'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── LOCK MODAL ── */}
        {lockModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-[#e2e8f0]">
              <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
                <h2 className="text-base font-bold text-[#1e293b] flex items-center gap-2">
                  <i className="ri-lock-2-line text-purple-600" /> Lock Reconciliation Period
                </h2>
                <button onClick={() => setLockModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
              <div className="p-6 space-y-4 text-xs">
                <div className={`p-4 rounded-xl border ${isZeroDifference ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  <p className="font-semibold text-sm text-[#1e293b]">
                    {formatDate(summary?.periodStart)} – {formatDate(summary?.periodEnd)}
                  </p>
                  <p className="mt-1">
                    <span className="text-slate-500">Difference: </span>
                    <span className={`font-semibold ${isZeroDifference ? 'text-green-700' : 'text-red-600'}`}>{formatINR(summary?.difference || 0)}</span>
                  </p>
                </div>
                {!isZeroDifference && (
                  <div>
                    <label className="block text-xs font-semibold text-amber-900 mb-1">Justification for locking with a difference *</label>
                    <textarea
                      rows={3}
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="e.g. Bank error reported to branch on 02-Nov, reference SR-1182"
                      className="w-full p-2.5 bg-white border border-amber-300 rounded-lg text-xs focus:outline-none focus:border-[#4f46e5]"
                    />
                  </div>
                )}
                <p className="text-slate-500 text-[11px]">
                  Once locked, statement lines cannot be changed and no manual entries can be posted to this bank account for the period. The statement
                  closing balance becomes next month's opening balance.
                </p>
              </div>
              <div className="px-6 py-4 border-t border-[#e2e8f0] bg-slate-50 flex items-center justify-end gap-3">
                <button onClick={() => setLockModalOpen(false)} className="h-9 px-4 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer">
                  Cancel
                </button>
                <button
                  onClick={handleLock}
                  disabled={locking || (!isZeroDifference && !overrideReason.trim())}
                  className="h-9 px-4 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 cursor-pointer disabled:opacity-50"
                >
                  {locking ? 'Locking…' : 'Confirm & Lock'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── BRS STATEMENT ── */}
        {reportOpen && summary && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setReportOpen(false)} />
            <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white z-50 overflow-y-auto shadow-2xl flex flex-col border-l border-[#e2e8f0]">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
                <div>
                  <h2 className="text-base font-bold text-[#1e293b]">Bank Reconciliation Statement</h2>
                  <p className="text-xs text-slate-500">As on {formatDate(summary.periodEnd)}</p>
                </div>
                <button onClick={() => setReportOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="flex-1 p-8 space-y-6 text-xs print-area" id="brs-printable-area">
                <div className="text-center border-b border-[#e2e8f0] pb-5">
                  <h2 className="text-lg font-bold text-[#1e293b]">BANK RECONCILIATION STATEMENT</h2>
                  <p className="text-sm font-semibold text-slate-800 mt-1">
                    {selectedBank?.bankName} — {selectedBank?.accountName}
                  </p>
                  <p className="text-xs font-mono text-slate-600">
                    A/c No: {selectedBank?.accountNumber} | IFSC: {selectedBank?.ifscCode || '—'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Period {formatDate(summary.periodStart)} to {formatDate(summary.periodEnd)}
                  </p>
                </div>

                <BrsSection
                  title="Balance as per Bank Statement"
                  opening={summary.closingBalanceBank}
                  groups={[
                    { label: 'ADD: Deposits in transit', sign: 1, items: depositsInTransit.map((d) => ({ id: d.id, text: `${formatDate(d.date)} — ${d.voucherNumber}: ${d.partyName || d.narration || ''}`, amount: d.debit })) },
                    { label: 'LESS: Cheques issued, not yet presented', sign: -1, items: outstandingCheques.map((c) => ({ id: c.id, text: `${formatDate(c.date)} — ${c.voucherNumber}: ${c.partyName || c.narration || ''}${c.chequeNo ? ` (Chq ${c.chequeNo})` : ''}`, amount: c.credit })) },
                    { label: 'LESS / ADD: Bank errors', sign: -1, items: bankErrors.map((e) => ({ id: e.id, text: `${formatDate(e.date)} — ${e.description}`, amount: e.credit - e.debit })) },
                  ]}
                  closingLabel="Adjusted Bank Balance"
                  closing={summary.adjustedBankBalance}
                />

                <BrsSection
                  title="Balance as per Cash Book"
                  opening={summary.closingBalanceBook}
                  groups={[
                    { label: 'ADD: Direct credits / interest not yet in books', sign: 1, items: bankOnlyCredits.map((s) => ({ id: s.id, text: `${formatDate(s.date)} — ${s.description}`, amount: s.credit })) },
                    { label: 'LESS: Bank charges / auto-debits not yet in books', sign: -1, items: bankOnlyDebits.map((s) => ({ id: s.id, text: `${formatDate(s.date)} — ${s.description}`, amount: s.debit })) },
                  ]}
                  closingLabel="Adjusted Cash Book Balance"
                  closing={summary.adjustedBookBalance}
                />

                <div className={`flex items-center justify-between p-4 rounded-xl border ${isZeroDifference ? 'bg-green-50 border-green-300 text-green-900' : 'bg-red-50 border-red-300 text-red-900'}`}>
                  <div className="flex items-center gap-2">
                    <i className={`text-xl ${isZeroDifference ? 'ri-checkbox-circle-fill text-green-600' : 'ri-error-warning-fill text-red-600'}`} />
                    <div>
                      <p className="font-bold text-sm">{isZeroDifference ? 'RECONCILED — Adjusted Bank = Adjusted Books' : 'NOT RECONCILED'}</p>
                      <p className="text-[11px]">
                        {isZeroDifference ? 'Every difference is explained.' : 'Investigate the remaining difference (wrong amounts, missing statement lines).'}
                      </p>
                    </div>
                  </div>
                  <span className="font-mono font-bold text-base">{formatINR(summary.difference)}</span>
                </div>
                {summary.status === 'LOCKED' && (
                  <p className="text-[11px] text-slate-500">
                    Locked on {formatDate(summary.lockedAt)} {summary.lockedByEmail ? `by ${summary.lockedByEmail}` : ''}
                    {summary.overrideReason ? ` — Override: ${summary.overrideReason}` : ''}
                  </p>
                )}
              </div>

              <div className="p-4 border-t border-[#e2e8f0] bg-slate-50 flex items-center justify-end">
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer"
                >
                  <i className="ri-printer-line" /> Print / Save PDF
                </button>
              </div>
            </div>
          </>
        )}

        <ConfirmDialog
          open={!!confirm}
          title={confirm?.title || ''}
          message={confirm?.message || ''}
          variant="warning"
          onConfirm={() => confirm?.onConfirm()}
          onCancel={() => setConfirm(null)}
        />
      </div>
    </AppLayout>
  );
}

function StatementPreview({
  rows,
  onClear,
  onRemove,
}: {
  rows: StatementImportRow[];
  onClear?: () => void;
  onRemove?: (index: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-green-700">✓ {rows.length} transactions ready to import</span>
        {onClear && (
          <button onClick={onClear} className="text-xs text-red-600 hover:underline cursor-pointer">
            Clear
          </button>
        )}
      </div>
      <div className="border border-[#e2e8f0] rounded-xl overflow-auto max-h-60 text-xs">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold sticky top-0">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Narration</th>
              <th className="px-3 py-2">Ref</th>
              <th className="px-3 py-2 text-right">Withdrawal</th>
              <th className="px-3 py-2 text-right">Deposit</th>
              <th className="px-3 py-2 text-right">Balance</th>
              {onRemove && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 whitespace-nowrap">{String(r.date)}</td>
                <td className="px-3 py-1.5 max-w-[220px] truncate">{r.description}</td>
                <td className="px-3 py-1.5 font-mono text-slate-500">{r.referenceNo || '—'}</td>
                <td className="px-3 py-1.5 text-right text-red-600">{r.debit > 0 ? formatINR(r.debit) : '—'}</td>
                <td className="px-3 py-1.5 text-right text-green-600">{r.credit > 0 ? formatINR(r.credit) : '—'}</td>
                <td className="px-3 py-1.5 text-right font-mono">{r.balance !== null && r.balance !== undefined ? formatINR(r.balance) : '—'}</td>
                {onRemove && (
                  <td className="px-3 py-1.5 text-center">
                    <button onClick={() => onRemove(i)} className="text-red-500 hover:text-red-700 cursor-pointer">
                      <i className="ri-delete-bin-line" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BrsSection({
  title,
  opening,
  groups,
  closingLabel,
  closing,
}: {
  title: string;
  opening: number;
  groups: { label: string; sign: 1 | -1; items: { id: string; text: string; amount: number }[] }[];
  closingLabel: string;
  closing: number;
}) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center py-2.5 border-b border-[#e2e8f0] text-sm font-bold">
        <span className="text-[#1e293b]">{title}</span>
        <span className="font-mono text-primary-600">{formatINR(opening)}</span>
      </div>
      {groups.map((g) => (
        <div key={g.label} className="pl-4 space-y-1">
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${g.sign > 0 ? 'text-green-700' : 'text-red-600'}`}>{g.label}</p>
          {g.items.length ? (
            g.items.map((it) => (
              <div key={it.id} className="flex justify-between items-center text-xs text-slate-700 pl-4 py-1 border-b border-slate-100 gap-3">
                <span className="truncate">{it.text}</span>
                <span className="font-mono font-semibold shrink-0">
                  {g.sign * it.amount >= 0 ? '+' : '−'}
                  {formatINR(Math.abs(it.amount))}
                </span>
              </div>
            ))
          ) : (
            <p className="text-[11px] text-slate-400 pl-4">None</p>
          )}
        </div>
      ))}
      <div className="flex justify-between items-center py-2.5 border-t-2 border-slate-300 text-sm font-bold bg-slate-50 px-4 rounded-xl">
        <span className="text-[#1e293b]">{closingLabel}</span>
        <span className="font-mono text-[#1e293b]">{formatINR(closing)}</span>
      </div>
    </div>
  );
}
